import { Button, Group, Modal, NumberInput, Tabs, TextInput } from "@mantine/core";
import {ArrowClockwiseIcon, FloppyDiskIcon, TrashIcon} from "@phosphor-icons/react";
import { useState } from "react";
import ProspectImport from "./ProspectImport.tsx";
import XmlImport from "./XmlImport.tsx";

interface AsteroidFieldControlsProps {
  disabled: boolean;
  currentName: string;
  renameField: (name: string) => void;
  importXml: (xml: string) => void;
  importEvents: (log: string) => void;
  resetScans: () => void;
  updateMaxDeposits: (maxDeposits: number) => void;
}

export default function AsteroidFieldControls({
  disabled,
  currentName,
  renameField,
  importXml,
  importEvents,
  resetScans,
  updateMaxDeposits,
}: AsteroidFieldControlsProps) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [maxDeposits, setMaxDeposits] = useState<number | string>("");
  const [name, setName] = useState(currentName);

  const submitMaxDeposits = () => {
    const numeric = Number(maxDeposits);
    if (!Number.isFinite(numeric) || numeric < 0) {
      return;
    }
    updateMaxDeposits(numeric);
  };

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== "" && trimmedName !== currentName;

  return (
    <>
      <Tabs defaultValue="events" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="events">Events Import</Tabs.Tab>
          <Tabs.Tab value="xml">XML Import</Tabs.Tab>
          <Tabs.Tab value="settings">Field Settings</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="events" pt="sm">
          <ProspectImport doImport={importEvents} disabled={disabled} />
        </Tabs.Panel>

        <Tabs.Panel value="xml" pt="sm">
          <XmlImport doImport={importXml} disabled={disabled} />
        </Tabs.Panel>

        <Tabs.Panel value="settings" pt="sm">
          <Group align="end" mb="sm">
            <TextInput
              label="Field name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              disabled={disabled}
              w={260}
            />
            <Button
              leftSection={<FloppyDiskIcon size={16} />}
              onClick={() => renameField(trimmedName)}
              disabled={disabled || !nameChanged}
            >
              Rename
            </Button>
          </Group>
          <Group align="end">
            <NumberInput
              label="Max deposits"
              min={0}
              value={maxDeposits}
              onChange={setMaxDeposits}
              disabled={disabled}
              w={180}
            />
            <Button
              leftSection={<FloppyDiskIcon size={16} />}
              onClick={submitMaxDeposits}
              disabled={disabled || maxDeposits === ""}
            >
              Save GPS Check
            </Button>
            <Button
              variant="outline"
              color="red"
              leftSection={<TrashIcon size={16} />}
              onClick={() => setShowResetConfirm(true)}
              disabled={disabled}
            >
              Reset Field
            </Button>
          </Group>
        </Tabs.Panel>
      </Tabs>

      <Modal
        opened={showResetConfirm}
        onClose={() => setShowResetConfirm(false)}
        title="Reset field data"
        centered
      >
        This clears the field layout, deposits and events. This action cannot be undone.
        <Group mt="md" justify="end">
          <Button variant="default" onClick={() => setShowResetConfirm(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<ArrowClockwiseIcon size={16} />}
            onClick={() => {
              resetScans();
              setShowResetConfirm(false);
            }}
          >
            Reset
          </Button>
        </Group>
      </Modal>
    </>
  );
}
